import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, RotateCcw, Sun, Moon } from "lucide-react";
import { useLocation } from "wouter";

// ── HSL ↔ Hex helpers ─────────────────────────────────────────────────────────

function hslStringToHex(hsl: string): string {
  const parts = hsl.trim().split(/\s+/);
  if (parts.length < 3) return "#808080";
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function hexToHslString(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// ── Default theme values (from index.css) ────────────────────────────────────

type ThemeColors = Record<string, string>;

const DEFAULT_LIGHT: ThemeColors = {
  "background": "188 20% 97%",
  "foreground": "200 14% 25%",
  "card": "188 18% 95%",
  "card-foreground": "200 14% 25%",
  "border": "188 18% 88%",
  "sidebar": "200 14% 25%",
  "sidebar-foreground": "198 15% 90%",
  "sidebar-primary": "192 44% 55%",
  "primary": "192 44% 55%",
  "primary-foreground": "200 14% 25%",
  "secondary": "188 18% 87%",
  "secondary-foreground": "200 14% 25%",
  "muted": "188 14% 89%",
  "muted-foreground": "200 14% 42%",
  "accent": "188 14% 88%",
  "accent-foreground": "200 14% 25%",
  "destructive": "0 72% 38%",
  "destructive-foreground": "0 85% 97%",
  "input": "188 14% 76%",
  "ring": "192 44% 55%",
};

const DEFAULT_DARK: ThemeColors = {
  "background": "200 14% 25%",
  "foreground": "198 15% 93%",
  "card": "200 14% 28%",
  "card-foreground": "198 15% 93%",
  "border": "200 14% 35%",
  "sidebar": "200 16% 20%",
  "sidebar-foreground": "198 15% 90%",
  "sidebar-primary": "192 44% 55%",
  "primary": "192 44% 55%",
  "primary-foreground": "202 35% 12%",
  "secondary": "200 14% 30%",
  "secondary-foreground": "198 15% 90%",
  "muted": "200 14% 28%",
  "muted-foreground": "198 10% 65%",
  "accent": "200 14% 29%",
  "accent-foreground": "198 15% 90%",
  "destructive": "0 72% 42%",
  "destructive-foreground": "0 85% 97%",
  "input": "200 14% 33%",
  "ring": "192 44% 55%",
};

// ── Color groups ──────────────────────────────────────────────────────────────

const GROUPS = [
  {
    label: "Page",
    items: [
      { key: "background", label: "Background" },
      { key: "foreground", label: "Text" },
      { key: "border", label: "Borders" },
      { key: "input", label: "Input Borders" },
      { key: "ring", label: "Focus Ring" },
    ],
  },
  {
    label: "Cards & Panels",
    items: [
      { key: "card", label: "Card Background" },
      { key: "card-foreground", label: "Card Text" },
    ],
  },
  {
    label: "Sidebar",
    items: [
      { key: "sidebar", label: "Sidebar Background" },
      { key: "sidebar-foreground", label: "Sidebar Text" },
      { key: "sidebar-primary", label: "Sidebar Highlight" },
    ],
  },
  {
    label: "Primary (Buttons & Links)",
    items: [
      { key: "primary", label: "Primary Colour" },
      { key: "primary-foreground", label: "Primary Text" },
    ],
  },
  {
    label: "Secondary",
    items: [
      { key: "secondary", label: "Secondary Background" },
      { key: "secondary-foreground", label: "Secondary Text" },
    ],
  },
  {
    label: "Subtle",
    items: [
      { key: "muted", label: "Muted Background" },
      { key: "muted-foreground", label: "Muted Text" },
      { key: "accent", label: "Accent Background" },
      { key: "accent-foreground", label: "Accent Text" },
    ],
  },
  {
    label: "Error / Danger",
    items: [
      { key: "destructive", label: "Error Colour" },
      { key: "destructive-foreground", label: "Error Text" },
    ],
  },
];

// ── Apply theme to document ───────────────────────────────────────────────────

function applyThemeToDocument(light: ThemeColors, dark: ThemeColors) {
  let styleEl = document.getElementById("custom-theme") as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "custom-theme";
    document.head.appendChild(styleEl);
  }
  const lightVars = Object.entries(light).map(([k, v]) => `  --${k}: ${v};`).join("\n");
  const darkVars = Object.entries(dark).map(([k, v]) => `  --${k}: ${v};`).join("\n");
  styleEl.textContent = `:root {\n${lightVars}\n}\n.dark {\n${darkVars}\n}`;
}

function applyThemeFromData(data: { light: ThemeColors; dark: ThemeColors } | null) {
  if (!data) return;
  applyThemeToDocument(data.light, data.dark);
}

// ── Color row component ───────────────────────────────────────────────────────

function ColorRow({
  label,
  hslValue,
  onChange,
}: {
  label: string;
  hslValue: string;
  onChange: (newHsl: string) => void;
}) {
  const hexValue = hslStringToHex(hslValue);
  const [hexInput, setHexInput] = useState(hexValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) setHexInput(hexValue);
  }, [hexValue, isFocused]);

  const commitHex = (raw: string) => {
    const val = raw.trim();
    const full = /^#[0-9a-fA-F]{6}$/.test(val) ? val
      : /^[0-9a-fA-F]{6}$/.test(val) ? `#${val}`
      : null;
    if (full) {
      onChange(hexToHslString(full));
      setHexInput(full);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground font-mono">{hslValue}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div
          className="h-8 w-8 rounded-md border shadow-sm cursor-pointer overflow-hidden relative shrink-0"
          title="Click to open colour picker"
          style={{ backgroundColor: hexValue }}
        >
          <input
            type="color"
            value={hexValue}
            onChange={(e) => {
              onChange(hexToHslString(e.target.value));
              setHexInput(e.target.value);
            }}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
          />
        </div>
        <input
          type="text"
          value={isFocused ? hexInput : hexValue}
          onChange={(e) => setHexInput(e.target.value)}
          onFocus={() => { setIsFocused(true); setHexInput(hexValue); }}
          onBlur={(e) => { setIsFocused(false); commitHex(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData("text").trim();
            setHexInput(pasted);
            commitHex(pasted);
          }}
          maxLength={7}
          placeholder="#000000"
          className="w-20 text-xs font-mono px-2 py-1 rounded-md border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          data-testid={`input-hex-${label.toLowerCase().replace(/\s+/g, "-")}`}
          spellCheck={false}
        />
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminThemePage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const { data: savedTheme } = useQuery<{ light: ThemeColors; dark: ThemeColors } | null>({
    queryKey: ["/api/theme"],
  });

  const [light, setLight] = useState<ThemeColors>(DEFAULT_LIGHT);
  const [dark, setDark] = useState<ThemeColors>(DEFAULT_DARK);
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (initialised) return;
    if (savedTheme !== undefined) {
      setLight(savedTheme ? { ...DEFAULT_LIGHT, ...savedTheme.light } : DEFAULT_LIGHT);
      setDark(savedTheme ? { ...DEFAULT_DARK, ...savedTheme.dark } : DEFAULT_DARK);
      setInitialised(true);
    }
  }, [savedTheme, initialised]);

  const applyLive = useCallback((l: ThemeColors, d: ThemeColors) => {
    applyThemeToDocument(l, d);
  }, []);

  const updateLight = (key: string, value: string) => {
    const next = { ...light, [key]: value };
    setLight(next);
    applyLive(next, dark);
  };

  const updateDark = (key: string, value: string) => {
    const next = { ...dark, [key]: value };
    setDark(next);
    applyLive(light, next);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", "/api/theme", { light, dark });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/theme"] });
      toast({ title: "Theme saved", description: "Your colour scheme is now live." });
    },
    onError: () => {
      toast({ title: "Save failed", variant: "destructive" });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/api/theme", {});
    },
    onSuccess: () => {
      setLight(DEFAULT_LIGHT);
      setDark(DEFAULT_DARK);
      applyLive(DEFAULT_LIGHT, DEFAULT_DARK);
      queryClient.invalidateQueries({ queryKey: ["/api/theme"] });
      toast({ title: "Theme reset", description: "Restored to default colours." });
    },
    onError: () => {
      toast({ title: "Reset failed", variant: "destructive" });
    },
  });

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/users")} data-testid="button-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Theme Editor</h1>
            <p className="text-sm text-muted-foreground">Changes apply instantly as a live preview. Save to make them permanent.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isPending}
            data-testid="button-reset-theme"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to defaults
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            data-testid="button-save-theme"
          >
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isPending ? "Saving..." : "Save theme"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="light">
        <TabsList className="w-full">
          <TabsTrigger value="light" className="flex-1 gap-2" data-testid="tab-light">
            <Sun className="h-4 w-4" />
            Light Mode
          </TabsTrigger>
          <TabsTrigger value="dark" className="flex-1 gap-2" data-testid="tab-dark">
            <Moon className="h-4 w-4" />
            Dark Mode
          </TabsTrigger>
        </TabsList>

        <TabsContent value="light" className="space-y-4 mt-4">
          {GROUPS.map((group) => (
            <Card key={group.label}>
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-base">{group.label}</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {group.items.map(({ key, label }) => (
                  <ColorRow
                    key={key}
                    label={label}
                    hslValue={light[key] ?? DEFAULT_LIGHT[key] ?? "0 0% 50%"}
                    onChange={(v) => updateLight(key, v)}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="dark" className="space-y-4 mt-4">
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
            <CardContent className="px-5 py-3">
              <CardDescription className="text-amber-800 dark:text-amber-300 text-sm">
                Switch your device or browser to dark mode to preview these colours live.
              </CardDescription>
            </CardContent>
          </Card>
          {GROUPS.map((group) => (
            <Card key={group.label}>
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-base">{group.label}</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {group.items.map(({ key, label }) => (
                  <ColorRow
                    key={key}
                    label={label}
                    hslValue={dark[key] ?? DEFAULT_DARK[key] ?? "0 0% 50%"}
                    onChange={(v) => updateDark(key, v)}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
